// Parametric node-graph engine — Grasshopper-style modular modeling.
//
// Core idea: a "graph" is a set of nodes wired together. Each node has typed
// input ports, typed output ports, and a `compute(inputs) → outputs` function.
// Evaluation runs in topological order from sources to the (single) Output
// node, producing a final triangle mesh that feeds the slicer.
//
// Why this design
// - Pure functional nodes: outputs depend only on inputs. No hidden state →
//   incremental re-eval on input change is trivial (bump a dirty flag).
// - Stable IDs (string), stable port names: graphs serialize cleanly to JSON
//   for save/load and undo/redo.
// - Type system uses runtime-checked `'number' | 'vec3' | 'mesh' | 'profile'`.
//   No TS generics on the value channel — keeps node definitions writable as
//   plain data without a compile step.

import type { Triangle } from './types';

export type PortType = 'number' | 'vec3' | 'mesh' | 'profile' | 'boolean';

export interface PortDef {
  id: string;             // unique within node, e.g. "x", "size", "in"
  name: string;           // user-visible label
  type: PortType;
  /** Default literal for unconnected inputs (numbers/vec3 only). */
  defaultValue?: unknown;
}

export interface NodeTypeDef {
  type: string;           // registry key, e.g. "primitive.box"
  category: string;       // for the add-menu grouping
  label: string;          // user-visible name in the canvas
  inputs: PortDef[];
  outputs: PortDef[];
  /** Pure compute. Inputs map: { portId → value }. Returns: { portId → value }. */
  compute: (inputs: Record<string, unknown>, params: Record<string, unknown>) => Record<string, unknown>;
  /** Inline parameter widgets (numbers, sliders) shown in the inspector when
   *  there's no upstream wire on that port. */
  params?: PortDef[];
}

export interface ParamNode {
  id: string;
  type: string;           // NodeTypeDef.type
  x: number;              // canvas position px
  y: number;
  /** Per-instance literal values for unwired params. */
  params: Record<string, unknown>;
  /** Semantic name for this specific instance. Overrides the type's generic
   *  label everywhere (overlay, canvas). E.g. a primitive.box used as a
   *  drawer face would have label "Çekmece yüzü", not "Kutu". Set by the
   *  LLM during generation so the user sees domain-relevant names. */
  label?: string;
  /** Per-port semantic names. E.g. for the same drawer-face box,
   *  portLabels.sx = "Çekmece genişliği". Overlay reads this first, falls
   *  back to the type's PortDef.name. */
  portLabels?: Record<string, string>;
  /** Logical group key used for clustering related parts in the overlay
   *  (e.g. all "Çekmece"-related nodes share group: "Çekmece"). Falls
   *  back to NodeTypeDef.category when absent. */
  group?: string;
}

export interface Wire {
  id: string;
  fromNode: string;
  fromPort: string;
  toNode: string;
  toPort: string;
}

export interface ParamGraph {
  nodes: ParamNode[];
  wires: Wire[];
}

let _idCounter = 0;
export function newId(prefix = 'n'): string {
  _idCounter = (_idCounter + 1) | 0;
  return `${prefix}${Date.now().toString(36)}${_idCounter.toString(36)}`;
}

export function emptyGraph(): ParamGraph {
  return { nodes: [], wires: [] };
}

/** Topological sort by Kahn's algorithm. Returns null on cycle. */
export function topoSort(graph: ParamGraph): ParamNode[] | null {
  const incoming = new Map<string, number>();
  const outgoing = new Map<string, string[]>();
  for (const n of graph.nodes) {
    incoming.set(n.id, 0);
    outgoing.set(n.id, []);
  }
  for (const w of graph.wires) {
    if (!incoming.has(w.toNode) || !outgoing.has(w.fromNode)) continue;
    incoming.set(w.toNode, (incoming.get(w.toNode) ?? 0) + 1);
    outgoing.get(w.fromNode)!.push(w.toNode);
  }
  const queue: string[] = [];
  for (const [id, c] of incoming) if (c === 0) queue.push(id);
  const order: ParamNode[] = [];
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  while (queue.length) {
    const id = queue.shift()!;
    const n = byId.get(id);
    if (n) order.push(n);
    for (const out of outgoing.get(id) ?? []) {
      const c = (incoming.get(out) ?? 1) - 1;
      incoming.set(out, c);
      if (c === 0) queue.push(out);
    }
  }
  if (order.length !== graph.nodes.length) return null; // cycle
  return order;
}

export interface EvalResult {
  /** Per-node, per-port computed value. */
  values: Map<string, Record<string, unknown>>;
  /** Final mesh: union of all "Output" node mesh inputs. Empty if no output node. */
  triangles: Triangle[];
  errors: { nodeId: string; message: string }[];
}

/** Evaluate a graph end-to-end. Returns triangles ready for the slicer. */
export function evalGraph(graph: ParamGraph, registry: Map<string, NodeTypeDef>): EvalResult {
  const order = topoSort(graph);
  const values = new Map<string, Record<string, unknown>>();
  const errors: { nodeId: string; message: string }[] = [];
  if (!order) {
    return { values, triangles: [], errors: [{ nodeId: '*', message: 'Cycle detected in graph' }] };
  }
  // Build incoming-wire lookup once: toNode → toPort → wire
  const incomingWires = new Map<string, Map<string, Wire>>();
  for (const w of graph.wires) {
    if (!incomingWires.has(w.toNode)) incomingWires.set(w.toNode, new Map());
    incomingWires.get(w.toNode)!.set(w.toPort, w);
  }
  for (const node of order) {
    const def = registry.get(node.type);
    if (!def) {
      errors.push({ nodeId: node.id, message: `Unknown node type: ${node.type}` });
      values.set(node.id, {});
      continue;
    }
    const inputs: Record<string, unknown> = {};
    const wires = incomingWires.get(node.id);
    for (const portDef of def.inputs) {
      const wire = wires?.get(portDef.id);
      if (wire) {
        const upstream = values.get(wire.fromNode);
        inputs[portDef.id] = upstream ? upstream[wire.fromPort] : undefined;
      } else {
        inputs[portDef.id] = node.params[portDef.id] ?? portDef.defaultValue;
      }
    }
    try {
      const out = def.compute(inputs, node.params);
      values.set(node.id, out);
    } catch (err) {
      errors.push({ nodeId: node.id, message: (err as Error).message });
      values.set(node.id, {});
    }
  }
  // Collect triangles from any node of type "output.mesh"
  const triangles: Triangle[] = [];
  for (const node of graph.nodes) {
    if (node.type !== 'output.mesh') continue;
    const wire = incomingWires.get(node.id)?.get('in');
    if (!wire) continue;
    const upstream = values.get(wire.fromNode);
    const mesh = upstream?.[wire.fromPort];
    if (Array.isArray(mesh)) {
      for (const t of mesh as Triangle[]) triangles.push(t);
    }
  }
  return { values, triangles, errors };
}

/** Translate triangles in-place along Z so the lowest vertex sits exactly at
 *  Z=0 (the build plate). No-op for empty input. Centers X/Y around origin
 *  too so the model doesn't drift off-plate when the user rotates the camera.
 *  Returns a NEW array (immutable triangle records). */
export function sitOnPlate(triangles: Triangle[]): Triangle[] {
  if (triangles.length === 0) return triangles;
  let minZ = Infinity, minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const t of triangles) {
    for (const v of [t.a, t.b, t.c]) {
      if (v[0] < minX) minX = v[0]; if (v[0] > maxX) maxX = v[0];
      if (v[1] < minY) minY = v[1]; if (v[1] > maxY) maxY = v[1];
      if (v[2] < minZ) minZ = v[2];
    }
  }
  const cx = (minX + maxX) * 0.5;
  const cy = (minY + maxY) * 0.5;
  // Skip work if already centered + on plate (avoids needless GC churn on hot eval loop).
  if (Math.abs(cx) < 0.01 && Math.abs(cy) < 0.01 && Math.abs(minZ) < 0.01) return triangles;
  return triangles.map((t) => ({
    a: [t.a[0] - cx, t.a[1] - cy, t.a[2] - minZ] as Vec3T,
    b: [t.b[0] - cx, t.b[1] - cy, t.b[2] - minZ] as Vec3T,
    c: [t.c[0] - cx, t.c[1] - cy, t.c[2] - minZ] as Vec3T,
    normal: t.normal
  }));
}

export type Vec3T = [number, number, number];
